import { useEffect } from "react";
import { useDarkMode } from "../../../../../context/ThemeContext";
import { useTranslation } from "react-i18next";
import CustomAlert from "../../../../../components/base/CustomAlert";

import { useModelForm } from "./hooks";
import {
  ModelHeader,
  ModelList,
  ModelPagination,
  ModelDetailsModal,
  ModelEditModal,
  ModelAddModal,
  CommandsModal,
} from "./components";

import type { ThemeClasses } from "./types";

export default function ModelsSection() {
  const { t } = useTranslation("resellerAdmin");
  const { isDarkMode } = useDarkMode();

  // Cleanup scroll lock on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const {
    // Data
    brands,
    meta,
    loading,
    selectedBrand,
    editForm,
    newBrandForm,
    alert,
    isSubmitting,

    // Modal states
    isModalOpen,
    isAddModalOpen,
    isCommandsModalOpen,
    modalType,

    // Filter states
    searchQuery,
    currentPage,
    selectedView,

    // Handlers
    handleSearchChange,
    handleEditClick,
    handleDetailsClick,
    handleCommandsClick,
    handleSaveChanges,
    handleAddBrand,
    setCurrentPage,
    setSelectedView,
    closeModal,
    closeAddModal,
    closeCommandsModal,
    openAddModal,
  } = useModelForm();

  const themeClasses: ThemeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-white",
    cardBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
    buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-100",
    buttonText: isDarkMode ? "text-gray-400" : "text-gray-600",
    buttonTextHover: isDarkMode ? "hover:text-white" : "hover:text-gray-900",
  };

  const totalPages = meta?.totalPages || 1;

  return (
    <div className={`h-full flex flex-col`}>
      {/* Alert */}
      {alert.show && (
        <CustomAlert
          title={alert.title}
          text={alert.message}
          icon={alert.type}
        />
      )}

      {/* Header */}
      <ModelHeader
        themeClasses={themeClasses}
        isDarkMode={isDarkMode}
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        selectedView={selectedView}
        setSelectedView={setSelectedView}
        onAddModel={openAddModal}
        t={t}
      />

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
          </div>
        ) : (
          <>
            <ModelList
              brands={brands}
              themeClasses={themeClasses}
              isDarkMode={isDarkMode}
              selectedView={selectedView}
              onEdit={handleEditClick}
              onDetails={handleDetailsClick}
              onCommands={handleCommandsClick}
              t={t}
            />

            <ModelPagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              t={t}
            />
          </>
        )}
      </div>

      {/* Details Modal */}
      {isModalOpen && modalType === "details" && selectedBrand && (
        <ModelDetailsModal
          brand={selectedBrand}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          onClose={closeModal}
          onEdit={handleEditClick}
        />
      )}

      {/* Edit Modal */}
      {isModalOpen && modalType === "edit" && selectedBrand && (
        <ModelEditModal
          brand={selectedBrand}
          editForm={editForm}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          isSubmitting={isSubmitting}
          onClose={closeModal}
          onSave={handleSaveChanges}
        />
      )}

      {/* Add Modal */}
      {isAddModalOpen && (
        <ModelAddModal
          formData={newBrandForm}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          isSubmitting={isSubmitting}
          onClose={closeAddModal}
          onSave={handleAddBrand}
        />
      )}

      {/* Commands Modal */}
      {isCommandsModalOpen && selectedBrand && (
        <CommandsModal
          brand={selectedBrand}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          onClose={closeCommandsModal}
        />
      )}
    </div>
  );
}
